import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "mt-1.5 w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

export default function PasswordChange({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast.error("新しいパスワードは8文字以上にしてください");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("新しいパスワード（確認）が一致しません");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("パスワードを変更しました");
      setOpen(false);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        data-testid={compact ? "password-change-button-mobile" : "password-change-button"}
        onClick={() => setOpen(true)}
        className="mt-1 flex w-full items-center gap-3 px-4 py-2 text-xs text-gray-500 transition-colors hover:text-accent"
      >
        <KeyRound className="h-3.5 w-3.5" />
        パスワード変更
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="password-change-form" onSubmit={submit} className="w-full max-w-md border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">パスワード変更</h2>
              <button type="button" data-testid="password-change-close" onClick={() => setOpen(false)} className="text-gray-500 transition-colors hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <label className="mt-6 block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">現在のパスワード</span>
              <input
                data-testid="password-current-input"
                type="password"
                required
                value={form.current}
                onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))}
                className={inputCls}
              />
            </label>
            <label className="mt-4 block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">新しいパスワード（8文字以上）</span>
              <input
                data-testid="password-new-input"
                type="password"
                required
                minLength={8}
                value={form.next}
                onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))}
                className={inputCls}
              />
            </label>
            <label className="mt-4 block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">新しいパスワード（確認）</span>
              <input
                data-testid="password-confirm-input"
                type="password"
                required
                minLength={8}
                value={form.confirm}
                onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                className={inputCls}
              />
            </label>
            <button
              data-testid="password-change-submit"
              type="submit"
              disabled={saving}
              className="mt-6 w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50"
            >
              {saving ? "変更中..." : "変更する"}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
